import { createContext, type ReactNode, useContext } from 'react';
import type { DaemonScope } from './daemon-queries.js';

export interface DaemonScopeValue {
  scope: DaemonScope;
  title: string;
  /** What one record is called in copy: "daemon" for the system scope, "employee" for an organization. */
  entityLabel: string;
  entityLabelPlural: string;
  /** The policy the server's routes for this scope require; gate every mutation control with it. */
  policy: 'RequireSuperadmin' | 'CanManageWorkforce';
  /** UI route prefix (e.g. for `Link` / `navigate`). */
  basePath: string;
  /** API route prefix for scope-specific endpoints not covered by the generated SDK (e.g. SSE streams). */
  apiBasePath: string;
}

export function buildDaemonScope(scope: DaemonScope): DaemonScopeValue {
  const isSystem = scope === 'system';
  return {
    scope,
    title: isSystem ? 'Daemons' : 'Workforce',
    entityLabel: isSystem ? 'daemon' : 'employee',
    entityLabelPlural: isSystem ? 'daemons' : 'employees',
    policy: isSystem ? 'RequireSuperadmin' : 'CanManageWorkforce',
    basePath: isSystem ? '/admin/daemons' : '/workforce',
    apiBasePath: isSystem ? '/admin/daemons' : '/workforce/employees',
  };
}

const DaemonScopeContext = createContext<DaemonScopeValue | null>(null);

interface DaemonScopeProviderProps {
  scope: DaemonScope;
  children: ReactNode;
}

export function DaemonScopeProvider({ scope, children }: DaemonScopeProviderProps) {
  return <DaemonScopeContext.Provider value={buildDaemonScope(scope)}>{children}</DaemonScopeContext.Provider>;
}

export function useDaemonScope(): DaemonScopeValue {
  const value = useContext(DaemonScopeContext);
  if (!value) throw new Error('useDaemonScope must be used within a <DaemonScopeProvider>');
  return value;
}
